/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { baseURLFromContext } from "./loader/loader.js";
// Helpers for BreadboardCapability
export const isBreadboardCapability = (o) => {
    const maybe = o;
    return !!maybe && maybe.kind === "board";
};
export const isGraphDescriptorCapability = (capability) => {
    const maybe = capability;
    const board = maybe.board;
    return !!board && !!board.edges && !!board.nodes;
};
export const isResolvedURLBoardCapability = (capability) => {
    const maybe = capability;
    const url = maybe.url;
    return !!url;
};
export const isUnresolvedPathBoardCapability = (capability) => {
    const maybe = capability;
    const path = maybe.path;
    return !!path;
};
const isGraphDescriptor = (candidate) => {
    const graph = candidate;
    return (graph && typeof graph === "object" && graph.nodes && graph.edges && true);
};
export const graphDescriptorFromCapability = async (capability, context) => {
    if (isGraphDescriptorCapability(capability)) {
        // If all we got is a GraphDescriptor, build a runnable board from it.
        // TODO: Use JSON schema to validate rather than this hack.
        return { success: true, graph: capability.board };
    }
    else if (isResolvedURLBoardCapability(capability)) {
        if (!context?.graphStore) {
            return {
                success: false,
                error: `The "board" Capability is a URL, but no graph store was supplied.`,
            };
        }
        const loaderResult = await context.graphStore.load(capability.url, context);
        if (!loaderResult.success) {
            return {
                success: false,
                error: `Unable to load "board" Capability with the URL of ${capability.url}: ${loaderResult.error}`,
            };
        }
        return loaderResult;
    }
    else if (isUnresolvedPathBoardCapability(capability)) {
        if (!context?.graphStore) {
            throw new Error(`The "board" Capability is a URL, but no graph store was supplied.`);
        }
        const loaderResult = await context.graphStore.load(capability.path, context);
        if (!loaderResult.success) {
            return {
                success: false,
                error: `Unable to load "board" Capability with the path of ${capability.path}: ${loaderResult.error}`,
            };
        }
        return loaderResult;
    }
    return {
        success: false,
        error: `Unsupported type of "board" Capability. Perhaps the supplied board isn't actually a GraphDescriptor?`,
    };
};
// TODO: Maybe this is just a GraphLoader API? Instead of taking a `string`,
// the `load` method should take an `unknown` that it then tries to
// shape-detect? And this is the code to do it.
export const getGraphDescriptor = async (board, context) => {
    if (!board)
        return { success: false, error: "No board provided" };
    if (typeof board === "string") {
        const loaderResult = await context?.graphStore?.load(board, context);
        if (!loaderResult?.success || !loaderResult.graph) {
            throw new Error(`Unable to load graph from "${board}"`);
        }
        return loaderResult;
    }
    else if (isBreadboardCapability(board)) {
        return graphDescriptorFromCapability(board, context);
    }
    else if (isGraphDescriptor(board)) {
        return { success: true, graph: board };
    }
    return { success: false, error: "Unable to get GraphDescriptor" };
};
const resolvePath = (capability, base) => {
    const path = capability.path;
    const url = new URL(path, base).href;
    return { kind: "board", url: url };
};
/**
 * Resolves any BreadboardCapability instances that are
 * `UnresolvedPathBoardCapability` to `ResolvedURLBoardCapability`.
 * This must happen at run-time, at the earliest moment when
 * the inputs are received by the BoardRunner.
 */
export const resolveBoardCapabilities = async (outputs, context, url) => {
    resolveBoardCapabilitiesInInputs(outputs, context, url);
    return Promise.resolve(outputs);
};
export const resolveBoardCapabilitiesInInputs = (values, context, url) => {
    for (const name in values) {
        const property = values[name];
        if (Array.isArray(property)) {
            values[name] = property.map((item) => resolveSingleInput(item));
        }
        else {
            values[name] = resolveSingleInput(property);
        }
    }
    return values;
    function resolveSingleInput(property) {
        if (isBreadboardCapability(property) &&
            isUnresolvedPathBoardCapability(property)) {
            const base = url ? new URL(url) : baseURLFromContext(context);
            return resolvePath(property, base);
        }
        return property;
    }
};
//# sourceMappingURL=capability.js.map